import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useGenerateFictitiousInvoices, useCalculateLineOptions, useProductSummary, useCheckMissingElectronicInvoices, useSyncMissingElectronicInvoices } from '../hooks/useInvoiceGeneration';

// SVG Icons
const PackageIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>);
const HashIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></svg>);
const EditIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>);
const CheckIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>);
const AlertIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>);
const InfoIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>);
const RefreshIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>);
const CloseIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>);

const GET_INVOICE_PREFIXES = gql`query GetInvoicePrefixes { getInvoicePrefixes { prefix resolutionNumber isActive } }`;

interface LineOption { linesPerInvoice: number; estimatedTotal: number; estimatedPerInvoice: number; productsUsed: number; }
interface Notification { id: string; type: 'success' | 'error' | 'warning' | 'info'; message: string; title?: string; }

export const GenerarFacturasPanel: React.FC = () => {
  const { generateFictitiousInvoices, loading: generationLoading, error: generationError } = useGenerateFictitiousInvoices();
  const { calculateOptions, loading: calculatingOptions } = useCalculateLineOptions();
  const { summary: productSummary } = useProductSummary();
  const { checkMissing, loading: checkingMissing } = useCheckMissingElectronicInvoices();
  const { syncMissing, loading: syncing } = useSyncMissingElectronicInvoices();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [syncStatus, setSyncStatus] = useState<{ totalInvoices: number; totalElectronic: number; missingCount: number; } | null>(null);
  const [syncStartDate, setSyncStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [syncStartTime, setSyncStartTime] = useState<string>('00:00');
  const [syncEndDate, setSyncEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [syncEndTime, setSyncEndTime] = useState<string>('23:59');
  const [useSyncDateFilter, setUseSyncDateFilter] = useState<boolean>(false);
  const [selectedPrefix, setSelectedPrefix] = useState<string>('');
  const [numberingMode, setNumberingMode] = useState<'automatic' | 'manual'>('automatic');
  const [manualStartNumber, setManualStartNumber] = useState<string>('');
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState<string>('');
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState<string>('');
  const [invoiceCount, setInvoiceCount] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState<string>('00:00');
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [endTime, setEndTime] = useState<string>('23:59');
  const [lineOptions, setLineOptions] = useState<LineOption[]>([]);
  const [selectedLineOption, setSelectedLineOption] = useState<number | null>(null);
  const [includeTip, setIncludeTip] = useState(false);
  const [tipPercentage, setTipPercentage] = useState<string>('10');
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState<any>(null);
  const [generationResult, setGenerationResult] = useState<any>(null);
  const processing = generationLoading;

  const { data: prefixesData, loading: prefixesLoading } = useQuery(GET_INVOICE_PREFIXES);
  const prefixes = prefixesData?.getInvoicePrefixes || [];

  const formatCurrency = (amount: number): string => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);

  const showNotification = useCallback((type: Notification['type'], message: string, title?: string) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type, message, title }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 4000);
  }, []);

  useEffect(() => { if (prefixes.length > 0 && !selectedPrefix) setSelectedPrefix(prefixes[0].prefix); }, [prefixes, selectedPrefix]);
  useEffect(() => { if (selectedPrefix) { setLastInvoiceNumber(`${selectedPrefix}075`); setNextInvoiceNumber(`${selectedPrefix}076`); } }, [selectedPrefix]);

  const handleGenerate = async () => {
    const count = parseInt(invoiceCount);
    if (!invoiceCount || count <= 0) { showNotification('warning', 'Cantidad inválida.'); return; }
    if (numberingMode === 'manual' && !manualStartNumber) { showNotification('warning', 'Número inicial requerido.'); return; }
    try {
      const result = await calculateOptions(count);
      if (result?.options) {
        setLineOptions(result.options.map((opt: any) => ({ linesPerInvoice: opt.linesPerInvoice, estimatedTotal: opt.estimatedTotal, estimatedPerInvoice: opt.estimatedPerInvoice, productsUsed: opt.productsUsed })));
        if (result.productSummary.totalProducts === 0) showNotification('warning', 'No hay productos disponibles.');
        else showNotification('info', `${result.productSummary.totalProducts} productos disponibles.`);
      }
    } catch { showNotification('error', 'Error al calcular opciones.'); }
  };

  const getEffectiveTotal = () => useCustomAmount && customAmount ? parseFloat(customAmount) : lineOptions.find(opt => opt.linesPerInvoice === selectedLineOption)?.estimatedTotal || 0;

  const handleProcessInvoices = () => {
    if (selectedLineOption === null) { showNotification('warning', 'Seleccione una opción.'); return; }
    if (useCustomAmount && (!customAmount || parseFloat(customAmount) <= 0)) { showNotification('warning', 'Monto inválido.'); return; }
    setConfirmData({ invoiceCount, selectedPrefix, numberingMode, nextInvoiceNumber, manualStartNumber, startDate, startTime, endDate, endTime, selectedLineOption, effectiveTotal: getEffectiveTotal(), useCustomAmount, includeTip, tipPercentage });
    setShowConfirmModal(true);
  };

  const confirmGeneration = async () => {
    setShowConfirmModal(false);
    try {
      const count = parseInt(invoiceCount);
      const result = await generateFictitiousInvoices({
        prefix: selectedPrefix, invoiceCount: count, startDate: new Date(`${startDate}T${startTime}`), endDate: new Date(`${endDate}T${endTime}`),
        linesPerInvoice: selectedLineOption!, includeTip, numberingMode, manualStartNumber: numberingMode === 'manual' ? parseInt(manualStartNumber) : undefined,
        customTotalAmount: useCustomAmount && customAmount ? parseFloat(customAmount) : undefined
      });

      // Paso 1: Verificar error de GraphQL/red (viene del hook)
      if (generationError) {
        showNotification('error', `Error de conexión: ${generationError.message}. Verifica que el backend esté corriendo.`);
        return;
      }

      // Paso 2: Verificar que la respuesta existe
      if (!result) {
        showNotification('error', 'No se recibió respuesta del servidor. Verifica la consola del backend para más detalles.');
        return;
      }

      // Paso 3: Verificar resultado exitoso
      if (result.success) {
        setGenerationResult({ success: true, invoicesCreated: result.generatedCount, firstInvoiceNumber: result.firstInvoiceNumber, lastInvoiceNumber: result.lastInvoiceNumber, totalAmount: result.totalAmount, message: result.message });
        setInvoiceCount(''); setLineOptions([]); setSelectedLineOption(null); setIncludeTip(false); setUseCustomAmount(false); setCustomAmount('');
        showNotification('success', `${result.generatedCount} facturas generadas.`);
      } else {
        showNotification('error', result.message || 'Error desconocido al generar facturas.');
      }
    } catch (e: any) {
      showNotification('error', e.message || 'Error inesperado. Revisa la consola del navegador y del backend.');
    }
  };

  const handleReset = () => { setInvoiceCount(''); setLineOptions([]); setSelectedLineOption(null); setIncludeTip(false); setTipPercentage('10'); setGenerationResult(null); setManualStartNumber(''); setUseCustomAmount(false); setCustomAmount(''); };

  const handleCheckSync = async () => {
    try {
      const result = await checkMissing(selectedPrefix || undefined, useSyncDateFilter ? new Date(`${syncStartDate}T${syncStartTime}`) : undefined, useSyncDateFilter ? new Date(`${syncEndDate}T${syncEndTime}`) : undefined);
      if (result) { setSyncStatus({ totalInvoices: result.totalInvoices, totalElectronic: result.totalElectronic, missingCount: result.missingCount }); showNotification(result.missingCount === 0 ? 'success' : 'warning', result.missingCount === 0 ? 'Todo sincronizado.' : `${result.missingCount} pendientes.`); }
    } catch (e: any) { showNotification('error', e.message); }
  };

  const handleSync = async () => {
    try {
      const result = await syncMissing(selectedPrefix || undefined, undefined, useSyncDateFilter ? new Date(`${syncStartDate}T${syncStartTime}`) : undefined, useSyncDateFilter ? new Date(`${syncEndDate}T${syncEndTime}`) : undefined);
      if (result?.success) { showNotification('success', result.message); handleCheckSync(); }
      else showNotification('error', result?.message || 'Error.');
    } catch (e: any) { showNotification('error', e.message); }
  };

  return (
    <div className="gfp-panel">
      <div className="gfp-header">
        <h2>Generar Facturas Ficticias</h2>
        <p>Crea facturas de prueba para completar numeración</p>
        {productSummary && (
          <div className="gfp-product-badge"><PackageIcon /><span>{productSummary.totalProducts} productos ({formatCurrency(productSummary.minPrice)} - {formatCurrency(productSummary.maxPrice)})</span></div>
        )}
      </div>

      {/* Step 1 */}
      <div className="gfp-card">
        <h3>1. Configuración</h3>
        <div className="gfp-form-group">
          <label>Prefijo</label>
          <select value={selectedPrefix} onChange={(e) => setSelectedPrefix(e.target.value)} disabled={processing || prefixesLoading}>
            {prefixesLoading ? <option>Cargando...</option> : prefixes.map((p: any) => <option key={p.prefix} value={p.prefix}>{p.prefix}{p.resolutionNumber ? ` (${p.resolutionNumber})` : ''}{!p.isActive ? ' - INACTIVA' : ''}</option>)}
          </select>
        </div>
        {selectedPrefix && (
          <div className="gfp-info-cards">
            <div className="gfp-info-card"><span>Última</span><strong>{lastInvoiceNumber}</strong></div>
            <div className="gfp-info-card highlight"><span>Siguiente</span><strong>{nextInvoiceNumber}</strong></div>
          </div>
        )}
        <div className="gfp-mode-selector">
          <button className={`gfp-mode-btn ${numberingMode === 'automatic' ? 'active' : ''}`} onClick={() => setNumberingMode('automatic')} disabled={processing}><HashIcon /> Automático</button>
          <button className={`gfp-mode-btn ${numberingMode === 'manual' ? 'active' : ''}`} onClick={() => setNumberingMode('manual')} disabled={processing}><EditIcon /> Manual</button>
        </div>
        {numberingMode === 'manual' && (
          <div className="gfp-form-group"><label>Número Inicial</label><input type="text" value={manualStartNumber} onChange={(e) => setManualStartNumber(e.target.value)} placeholder="Ej: 100" disabled={processing} /><small>Sin prefijo. Ej: 100 para {selectedPrefix}100</small></div>
        )}
      </div>

      {/* Step 2 */}
      <div className="gfp-card">
        <h3>2. Cantidad y Fechas</h3>
        <div className="gfp-form-group"><label>Cantidad</label><input type="number" value={invoiceCount} onChange={(e) => setInvoiceCount(e.target.value)} placeholder="Ej: 25" min="1" max="500" disabled={processing} /><small>Máx. 500</small></div>
        <div className="gfp-date-grid">
          <div className="gfp-form-group"><label>Fecha Inicio</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={processing} /></div>
          <div className="gfp-form-group"><label>Hora</label><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} disabled={processing} /></div>
          <div className="gfp-form-group"><label>Fecha Fin</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={processing} /></div>
          <div className="gfp-form-group"><label>Hora</label><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} disabled={processing} /></div>
        </div>
        <button className="gfp-btn gfp-btn-primary gfp-btn-lg" onClick={handleGenerate} disabled={processing || calculatingOptions || !invoiceCount}>
          {calculatingOptions ? 'Calculando...' : 'Generar Opciones'}
        </button>
      </div>

      {/* Step 3 */}
      {lineOptions.length > 0 && (
        <div className="gfp-card">
          <h3>3. Opciones de Líneas</h3>
          <div className="gfp-options-grid">
            {lineOptions.map((opt) => (
              <div key={opt.linesPerInvoice} className={`gfp-option-card ${selectedLineOption === opt.linesPerInvoice ? 'selected' : ''}`} onClick={() => { setSelectedLineOption(opt.linesPerInvoice); if (!useCustomAmount) setCustomAmount(''); }}>
                <span className="gfp-opt-num">{opt.linesPerInvoice}</span>
                <span className="gfp-opt-label">línea(s)</span>
                <span className="gfp-opt-total">{formatCurrency(opt.estimatedTotal)}</span>
                {selectedLineOption === opt.linesPerInvoice && <CheckIcon />}
              </div>
            ))}
          </div>

          <div className="gfp-custom-section">
            <label className="gfp-checkbox"><input type="checkbox" checked={useCustomAmount} onChange={(e) => { setUseCustomAmount(e.target.checked); if (e.target.checked && !selectedLineOption) { const d = lineOptions.find(o => o.linesPerInvoice === 3) || lineOptions[0]; if (d) setSelectedLineOption(d.linesPerInvoice); } }} disabled={processing} /> Monto personalizado</label>
            {useCustomAmount && (
              <div className="gfp-custom-inputs">
                <div className="gfp-form-group"><label>Monto Total (COP)</label><input type="number" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder="Ej: 5000000" min="0" step="10000" disabled={processing} /></div>
                <div className="gfp-form-group"><label>Líneas</label><select value={selectedLineOption || ''} onChange={(e) => setSelectedLineOption(parseInt(e.target.value))} disabled={processing}>{lineOptions.map((o) => <option key={o.linesPerInvoice} value={o.linesPerInvoice}>{o.linesPerInvoice} línea(s)</option>)}</select></div>
              </div>
            )}
          </div>

          <div className="gfp-tip-section">
            <label className="gfp-checkbox"><input type="checkbox" checked={includeTip} onChange={(e) => setIncludeTip(e.target.checked)} disabled={processing} /> Incluir propina</label>
            {includeTip && (
              <div className="gfp-form-group gfp-tip-input"><label>%</label><input type="number" value={tipPercentage} onChange={(e) => setTipPercentage(e.target.value)} min="0" max="50" disabled={processing} /></div>
            )}
          </div>

          {selectedLineOption !== null && (
            <div className="gfp-preview">
              <h4>Resumen</h4>
              <div className="gfp-preview-grid">
                <div><span>Facturas</span><strong>{invoiceCount}</strong></div>
                <div><span>Líneas</span><strong>{selectedLineOption}</strong></div>
                <div><span>Período</span><strong>{startDate} - {endDate}</strong></div>
                <div className="total"><span>{useCustomAmount ? 'Monto' : 'Estimado'}</span><strong>{formatCurrency(getEffectiveTotal())}</strong></div>
              </div>
            </div>
          )}

          <div className="gfp-actions">
            <button className="gfp-btn gfp-btn-secondary" onClick={handleReset} disabled={processing}>Reiniciar</button>
            <button className="gfp-btn gfp-btn-primary" onClick={handleProcessInvoices} disabled={processing || selectedLineOption === null}>{processing ? 'Procesando...' : 'Generar'}</button>
          </div>
        </div>
      )}

      {/* Results */}
      {generationResult && (
        <div className="gfp-card gfp-success-card">
          <h3><CheckIcon /> Completado</h3>
          <p>{generationResult.message}</p>
          <div className="gfp-summary-grid">
            <div><span>Creadas</span><strong>{generationResult.invoicesCreated}</strong></div>
            <div><span>Primera</span><strong>{generationResult.firstInvoiceNumber}</strong></div>
            <div><span>Última</span><strong>{generationResult.lastInvoiceNumber}</strong></div>
            <div><span>Total</span><strong>{formatCurrency(generationResult.totalAmount)}</strong></div>
          </div>
          <button className="gfp-btn gfp-btn-primary" onClick={handleReset}>Nueva Generación</button>
        </div>
      )}

      {/* Sync Section */}
      <div className="gfp-card gfp-sync-card">
        <h3><RefreshIcon /> Sincronización Electrónica</h3>
        <p>Verifica facturas pendientes de sincronizar con DIAN</p>
        <label className="gfp-checkbox"><input type="checkbox" checked={useSyncDateFilter} onChange={(e) => setUseSyncDateFilter(e.target.checked)} disabled={checkingMissing || syncing} /> Filtrar por fecha</label>
        {useSyncDateFilter && (
          <div className="gfp-date-grid">
            <div className="gfp-form-group"><label>Desde</label><input type="date" value={syncStartDate} onChange={(e) => setSyncStartDate(e.target.value)} disabled={checkingMissing || syncing} /></div>
            <div className="gfp-form-group"><label>Hora</label><input type="time" value={syncStartTime} onChange={(e) => setSyncStartTime(e.target.value)} disabled={checkingMissing || syncing} /></div>
            <div className="gfp-form-group"><label>Hasta</label><input type="date" value={syncEndDate} onChange={(e) => setSyncEndDate(e.target.value)} disabled={checkingMissing || syncing} /></div>
            <div className="gfp-form-group"><label>Hora</label><input type="time" value={syncEndTime} onChange={(e) => setSyncEndTime(e.target.value)} disabled={checkingMissing || syncing} /></div>
          </div>
        )}
        <div className="gfp-sync-actions">
          <button className="gfp-btn gfp-btn-secondary" onClick={handleCheckSync} disabled={checkingMissing || syncing}>{checkingMissing ? 'Verificando...' : 'Verificar'}</button>
          {syncStatus && syncStatus.missingCount > 0 && <button className="gfp-btn gfp-btn-primary" onClick={handleSync} disabled={syncing || checkingMissing}>{syncing ? 'Sincronizando...' : `Sincronizar ${syncStatus.missingCount}`}</button>}
        </div>
        {syncStatus && (
          <div className="gfp-sync-stats">
            <div><span>Total</span><strong>{syncStatus.totalInvoices}</strong></div>
            <div><span>Electrónicas</span><strong>{syncStatus.totalElectronic}</strong></div>
            <div className={syncStatus.missingCount > 0 ? 'warn' : 'ok'}><span>Pendientes</span><strong>{syncStatus.missingCount}</strong></div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="gfp-card gfp-info-card">
        <h4><InfoIcon /> Información</h4>
        <ul>
          <li><strong>Productos:</strong> Se usan productos reales del sistema</li>
          <li><strong>Monto Personalizado:</strong> Ajusta cantidades automáticamente</li>
          <li><strong>Fechas:</strong> Distribución cronológica en el rango</li>
          <li><strong>Irreversible:</strong> Las facturas quedan permanentes</li>
        </ul>
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && confirmData && (
        <div className="gfp-modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="gfp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gfp-modal-icon"><AlertIcon /></div>
            <h3>Confirmar Generación</h3>
            <p>¿Generar <strong>{confirmData.invoiceCount} facturas</strong>?</p>
            <div className="gfp-modal-details">
              <div><span>Prefijo:</span><strong>{confirmData.selectedPrefix}</strong></div>
              <div><span>Inicio:</span><strong>{confirmData.numberingMode === 'automatic' ? confirmData.nextInvoiceNumber : confirmData.manualStartNumber}</strong></div>
              <div><span>Período:</span><strong>{confirmData.startDate} - {confirmData.endDate}</strong></div>
              <div className="total"><span>Total:</span><strong>{formatCurrency(confirmData.effectiveTotal)}</strong></div>
            </div>
            <p className="gfp-modal-warn">No se puede deshacer</p>
            <div className="gfp-modal-actions">
              <button className="gfp-btn gfp-btn-primary" onClick={confirmGeneration} disabled={processing}>{processing ? 'Generando...' : 'Confirmar'}</button>
              <button className="gfp-btn gfp-btn-secondary" onClick={() => setShowConfirmModal(false)} disabled={processing}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      <div className="gfp-notifications">
        {notifications.map((n) => (
          <div key={n.id} className={`gfp-notif gfp-notif-${n.type}`}>
            <div className="gfp-notif-content">{n.title && <strong>{n.title}</strong>}{n.message}</div>
            <button onClick={() => setNotifications(prev => prev.filter(x => x.id !== n.id))}><CloseIcon /></button>
          </div>
        ))}
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .gfp-panel { max-width: 900px; margin: 0 auto; }

  .gfp-header {
    padding: 16px 20px;
    background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%);
    border-radius: 8px;
    color: #fff;
    margin-bottom: 16px;
  }
  .gfp-header h2 { margin: 0 0 4px 0; font-size: 1rem; }
  .gfp-header p { margin: 0; font-size: 0.75rem; opacity: 0.9; }

  .gfp-product-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255,255,255,0.2);
    padding: 6px 12px;
    border-radius: 16px;
    margin-top: 10px;
    font-size: 0.7rem;
  }

  .gfp-card {
    background: #fff;
    border: 1px solid #dbeafe;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
  }
  .gfp-card h3 { margin: 0 0 12px 0; font-size: 0.85rem; color: #1e3a8a; display: flex; align-items: center; gap: 6px; }
  .gfp-card h4 { margin: 0 0 10px 0; font-size: 0.8rem; color: #1e3a8a; display: flex; align-items: center; gap: 6px; }
  .gfp-card p { margin: 0 0 12px 0; font-size: 0.75rem; color: #64748b; }

  .gfp-form-group { margin-bottom: 12px; }
  .gfp-form-group label { display: block; font-size: 0.7rem; color: #64748b; margin-bottom: 4px; }
  .gfp-form-group input, .gfp-form-group select {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    font-size: 0.8rem;
  }
  .gfp-form-group small { font-size: 0.65rem; color: #94a3b8; }

  .gfp-info-cards { display: flex; gap: 10px; margin: 12px 0; }
  .gfp-info-card {
    flex: 1;
    text-align: center;
    padding: 10px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
  }
  .gfp-info-card span { display: block; font-size: 0.65rem; color: #64748b; }
  .gfp-info-card strong { font-size: 0.9rem; color: #1e3a8a; }
  .gfp-info-card.highlight { background: #eff6ff; border-color: #93c5fd; }

  .gfp-mode-selector { display: flex; gap: 8px; margin: 12px 0; }
  .gfp-mode-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px;
    border: 2px solid #dbeafe;
    border-radius: 6px;
    background: #fff;
    color: #64748b;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.15s;
  }
  .gfp-mode-btn:hover { border-color: #3b82f6; color: #1e40af; }
  .gfp-mode-btn.active { background: #2563eb; border-color: #2563eb; color: #fff; }

  .gfp-date-grid { display: grid; grid-template-columns: 1fr 80px 1fr 80px; gap: 8px; margin-bottom: 12px; }

  .gfp-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }
  .gfp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .gfp-btn-primary { background: #2563eb; color: #fff; }
  .gfp-btn-primary:hover:not(:disabled) { background: #1e40af; }
  .gfp-btn-secondary { background: #e2e8f0; color: #475569; }
  .gfp-btn-secondary:hover:not(:disabled) { background: #cbd5e1; }
  .gfp-btn-lg { width: 100%; padding: 12px; font-size: 0.8rem; }

  .gfp-options-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
  .gfp-option-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px;
    border: 2px solid #dbeafe;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s;
    position: relative;
  }
  .gfp-option-card:hover { border-color: #3b82f6; }
  .gfp-option-card.selected { border-color: #2563eb; background: #eff6ff; }
  .gfp-option-card svg { position: absolute; top: 6px; right: 6px; color: #2563eb; }
  .gfp-opt-num { font-size: 1.2rem; font-weight: 700; color: #1e3a8a; }
  .gfp-opt-label { font-size: 0.65rem; color: #64748b; }
  .gfp-opt-total { font-size: 0.7rem; color: #1e40af; font-weight: 600; margin-top: 4px; }

  .gfp-custom-section, .gfp-tip-section {
    padding: 12px;
    background: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 6px;
    margin-bottom: 12px;
  }

  .gfp-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
    color: #475569;
    cursor: pointer;
  }
  .gfp-checkbox input { width: 16px; height: 16px; }

  .gfp-custom-inputs { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 12px; }
  .gfp-tip-input { max-width: 100px; margin-top: 8px; }

  .gfp-preview {
    padding: 12px;
    background: #eff6ff;
    border-radius: 6px;
    margin-bottom: 12px;
  }
  .gfp-preview h4 { margin-bottom: 10px; }
  .gfp-preview-grid, .gfp-summary-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }
  .gfp-preview-grid > div, .gfp-summary-grid > div {
    text-align: center;
    padding: 8px;
    background: #fff;
    border-radius: 4px;
  }
  .gfp-preview-grid span, .gfp-summary-grid span { display: block; font-size: 0.65rem; color: #64748b; }
  .gfp-preview-grid strong, .gfp-summary-grid strong { font-size: 0.85rem; color: #1e3a8a; }
  .gfp-preview-grid .total { background: #dbeafe; }

  .gfp-actions { display: flex; justify-content: flex-end; gap: 8px; }

  .gfp-success-card { border-left: 4px solid #22c55e; }
  .gfp-success-card h3 { color: #166534; }
  .gfp-success-card > p { color: #166534; }

  .gfp-sync-card { border-left: 4px solid #22c55e; background: #f0fdf4; }
  .gfp-sync-card h3 { color: #166534; }
  .gfp-sync-actions { display: flex; gap: 8px; margin: 12px 0; }
  .gfp-sync-stats { display: flex; gap: 16px; padding: 10px; background: rgba(255,255,255,0.7); border-radius: 6px; }
  .gfp-sync-stats > div { display: flex; flex-direction: column; }
  .gfp-sync-stats span { font-size: 0.65rem; color: #64748b; }
  .gfp-sync-stats strong { font-size: 1rem; color: #1e3a8a; }
  .gfp-sync-stats .warn strong { color: #f59e0b; }
  .gfp-sync-stats .ok strong { color: #22c55e; }

  .gfp-info-card { background: #eff6ff; border-color: #93c5fd; }
  .gfp-info-card h4 { color: #1e40af; }
  .gfp-info-card ul { margin: 0; padding-left: 16px; font-size: 0.7rem; color: #1e3a8a; }
  .gfp-info-card li { margin-bottom: 4px; }

  .gfp-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  .gfp-modal {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    max-width: 400px;
    text-align: center;
  }
  .gfp-modal-icon { color: #f59e0b; margin-bottom: 12px; }
  .gfp-modal-icon svg { width: 48px; height: 48px; }
  .gfp-modal h3 { margin: 0 0 8px 0; color: #1e3a8a; }
  .gfp-modal p { margin: 0 0 12px 0; font-size: 0.85rem; color: #475569; }
  .gfp-modal-details {
    background: #f8fafc;
    border-radius: 8px;
    padding: 12px;
    text-align: left;
    margin-bottom: 12px;
  }
  .gfp-modal-details > div { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #e2e8f0; font-size: 0.8rem; }
  .gfp-modal-details > div:last-child { border: none; }
  .gfp-modal-details .total { margin-top: 8px; padding-top: 10px; border-top: 2px solid #e2e8f0; }
  .gfp-modal-details .total strong { color: #22c55e; }
  .gfp-modal-warn { font-size: 0.75rem; color: #94a3b8; font-style: italic; }
  .gfp-modal-actions { display: flex; justify-content: center; gap: 10px; }

  .gfp-notifications { position: fixed; top: 80px; right: 20px; z-index: 2000; display: flex; flex-direction: column; gap: 10px; max-width: 350px; }
  .gfp-notif {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    animation: gfp-slide 0.3s ease;
  }
  @keyframes gfp-slide { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  .gfp-notif-success { background: #dcfce7; border: 1px solid #22c55e; color: #166534; }
  .gfp-notif-error { background: #fee2e2; border: 1px solid #ef4444; color: #b91c1c; }
  .gfp-notif-warning { background: #fef3c7; border: 1px solid #f59e0b; color: #92400e; }
  .gfp-notif-info { background: #dbeafe; border: 1px solid #3b82f6; color: #1e40af; }
  .gfp-notif-content { flex: 1; font-size: 0.8rem; }
  .gfp-notif-content strong { display: block; margin-bottom: 2px; }
  .gfp-notif button { background: none; border: none; cursor: pointer; opacity: 0.6; padding: 4px; }
  .gfp-notif button:hover { opacity: 1; }

  @media (max-width: 700px) {
    .gfp-date-grid { grid-template-columns: 1fr 1fr; }
    .gfp-options-grid { grid-template-columns: repeat(2, 1fr); }
    .gfp-preview-grid, .gfp-summary-grid { grid-template-columns: repeat(2, 1fr); }
    .gfp-custom-inputs { grid-template-columns: 1fr; }
  }
`;
